import { ISection } from "../../interfaces";

/**
 * Converts a UUID-based object structure into a simplified structure without UUID keys.
 *
 * @param convertedSections - An array of sections containing UUID-based object structures.
 * @returns A new array of sections with UUID keys removed and content transformed.
 */
export const convertFromUUIDKeys = (convertedSections: ISection[]) => {
  /**
   * Converts a UUID object into an array of columns, optionally handling subsections.
   *
   * @param uuidObject - The UUID-based object to process.
   * @param isSubSect - A boolean indicating if the object is part of a subsection. Defaults to `false`.
   * @returns An array of columns with UUID keys removed.
   */
  const colsFromUUIDObject = (
    uuidObject: Record<string, any>,
    isSubSect?: boolean,
  ) => {
    return Object.values(uuidObject).map(objWithoutIds => {
      if (
        isSubSect &&
        objWithoutIds.type === "split" &&
        objWithoutIds.unit === "sub"
      ) {
        return {
          ...objWithoutIds,
          cols: Object.values(objWithoutIds.cols),
        };
      } else if (objWithoutIds.unit === "guernseyMulti") {
        const newContent: any = {};
        Object.entries(objWithoutIds.content).forEach(([key, row]) => {
          if (key === "cols") return;
          newContent[String(key)] = (row as any[]).map(item => ({
            ...item,
            cols: Object.values(item.cols),
          }));
        });
        return {
          ...objWithoutIds,
          content: newContent,
        };
      } else if ("content" in objWithoutIds) {
        return {
          ...objWithoutIds,
          content: {
            ...objWithoutIds.content,
            cols: Object.values(objWithoutIds.content.cols),
          },
        };
      }
      return objWithoutIds;
    });
  };

  /**
   * Maps a normal section by transforming its rows and subrows.
   *
   * @param section - The section object to map.
   * @returns A new section object with transformed rows and subrows.
   */
  const mapNormalSection = (section: any) => ({
    ...section,
    rows: section.rows.map((row: any) => {
      if (row.cols) {
        const cols = colsFromUUIDObject(row.cols);
        if (row.date || row.dateUTC) {
          return { cols, date: row.date, dateUTC: row.dateUTC };
        }
        return cols;
      }
      if (row.subrows) {
        const subrows = row.subrows.map((subrow: any) =>
          colsFromUUIDObject(subrow.cols),
        );
        if (row.date || row.dateUTC) {
          return { subrows, date: row.date, dateUTC: row.dateUTC };
        }
        return subrows;
      }
      if (row.items) {
        return {
          ...row,
          items: Object.values(row.items).map((item: any) => ({
            ...item,
            cols: colsFromUUIDObject(item.cols, true),
          })),
        };
      }
      return row;
    }),
  });

  return convertedSections.map(section => {
    if (section.subSections) {
      return {
        ...section,
        subSections: section.subSections.map(mapNormalSection),
      };
    } else if (section.shared && section.sections) {
      return {
        ...section,
        sections: section.sections.map(mapNormalSection),
      };
    } else {
      return mapNormalSection(section);
    }
  });
};
